// Pestaña de facturación, a grano FACTURA.
//
// Aquí no se repiten las líneas: eso lo hace la pestaña Líneas, a su grano y con
// sus columnas. Esta responde otra pregunta —cuántas facturas hay, de cuándo y
// por cuánto— y ofrece el puente: «ver sus líneas» salta a la otra pestaña ya
// filtrada, en vez de pintar una segunda tabla igual.
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { agruparPorFactura, totalFacturado } from '@/utils/facturasPedido'
import { FacturasPedido } from '../FacturasPedido'
import { fmtEur, fmtQty } from '../formato'
import { BarraMarcas, useMarcasFilas } from '@/components/common/MarcasFilas'
import { claveFila } from '@/utils/marcasFilas'
import { PieTotales, VacioDiscreto, campoCls, type DatoFranja } from './controles'
import { apagadoCls, bordeCls, btnNeutroCls, rotuloCls, textoCls } from './estilos'
import type { PedidoVentaCabeceraVM, PedidoVentaLinea } from '@/types'

interface Props {
  dark: boolean
  cabecera: PedidoVentaCabeceraVM
  lineas: PedidoVentaLinea[]
  onVerAdjuntosFactura: (recid: string, factura: string) => void
  onVerLineas: (factura: string) => void
}

export function SeccionFacturacion({
  dark, cabecera, lineas, onVerAdjuntosFactura, onVerLineas,
}: Props) {
  const { t } = useTranslation()
  const [busqueda, setBusqueda] = useState('')

  // ── Marcas de fila ──────────────────────────────────────────────────────────
  // Ámbito propio del pedido y de esta pestaña, por lo mismo que en Cargas: la
  // franja de recuento y su «quitar todas» viven aquí dentro, así que sólo
  // pueden hablar de las facturas que se están viendo. Compartirlo con Líneas
  // haría que el contador sumara filas de otra pestaña.
  const marcas = useMarcasFilas(`pedidoVentaFacturas:${cabecera.empresa}/${cabecera.salesid}`)
  // Mismo esquema de clave que la lista de pedidos: empresa + nº de pedido, y
  // encima el nº de factura. La factura F-1 de este pedido nunca puede confundirse
  // con la del pedido de al lado.
  const clavePedido = useMemo(
    () => claveFila(cabecera.empresa, cabecera.salesid),
    [cabecera.empresa, cabecera.salesid],
  )

  const facturas = useMemo(() => agruparPorFactura(lineas), [lineas])
  const total = useMemo(() => totalFacturado(lineas), [lineas])

  // Se filtran las LÍNEAS y no las facturas ya agrupadas, porque el componente
  // de abajo agrupa por su cuenta: pasarle sólo las filas que interesan es lo
  // que hace que su resumen y sus filas sigan cuadrando entre sí.
  const lineasVisibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase()
    if (!q) return lineas
    return lineas.filter(l => (l.factura ?? '').toLowerCase().includes(q))
  }, [lineas, busqueda])

  const facturasVisibles = useMemo(() => agruparPorFactura(lineasVisibles), [lineasVisibles])
  const visibles = facturasVisibles.length

  // Totalizador de las filas VISIBLES. Sólo entra lo sumable a nivel factura:
  // qty e importe facturados, que son por fila. El importe pendiente NO baja
  // aquí — viene de la cabecera del pedido y se queda arriba, donde no invita
  // a leerse como la suma de una columna.
  const totales: DatoFranja[] = useMemo(() => {
    const totalVisible = totalFacturado(lineasVisibles)
    return [
      { clave: 'n', valor: String(facturasVisibles.length), etiqueta: t('salesOrders.detalle.facturasEtiqueta') },
      { clave: 'ud', valor: fmtQty(totalVisible.facturada), etiqueta: t('salesOrders.picking.units') },
      { clave: 'imp', valor: fmtEur(totalVisible.importe), etiqueta: '' },
    ]
  }, [lineasVisibles, facturasVisibles.length, t])

  // Una línea de resumen, no tres tarjetas: son cuatro cifras y meterlas en
  // cajas separadas les daría un peso que no tienen frente a la lista.
  const cifra = (etiqueta: string, valor: string, fuerte = false) => (
    <div className="flex items-baseline gap-2">
      <span className={rotuloCls(dark)}>{etiqueta}</span>
      <span className={`tabular-nums ${
        fuerte
          ? `text-[16px] font-bold ${dark ? 'text-slate-50' : 'text-slate-900'}`
          : `text-[15px] font-semibold ${textoCls(dark)}`
      }`}>
        {valor}
      </span>
    </div>
  )

  return (
    <div className="flex flex-col gap-3">
      <div className={`flex items-baseline gap-x-8 gap-y-2 flex-wrap pb-3 border-b ${bordeCls(dark)}`}>
        {cifra(t('salesOrders.billing.count', { count: facturas.length }).replace(/^\d+\s*/, ''), String(facturas.length), true)}
        {cifra(t('salesOrders.invoice.billedQty'), `${fmtQty(total.facturada)} ${t('salesOrders.picking.units')}`)}
        {cifra(t('salesOrders.detalle.importeFacturado'), fmtEur(total.importe))}
        {/* El pendiente sale de la cabecera del pedido, que es donde vive: no se
            deriva restando importes de facturas. */}
        {cabecera.importePendiente > 0 && cifra(
          t('salesOrders.col.pendingAmount'),
          fmtEur(cabecera.importePendiente),
        )}

        {facturas.length > 3 && (
          <input
            type="search"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder={t('salesOrders.detalle.buscarFactura')}
            className={`${campoCls(dark)} ml-auto w-[200px]`}
          />
        )}
      </div>

      {busqueda && (
        <span className={`text-[11px] tabular-nums ${apagadoCls(dark)}`}>
          {t('salesOrders.detalle.mostrando', { n: visibles, total: facturas.length })}
        </span>
      )}

      {/* Fuera de la tarjeta de la lista a propósito: si la búsqueda deja cero
          facturas visibles esa tarjeta no se pinta, y con la franja dentro el
          usuario se quedaría con marcas puestas y sin el botón de quitarlas. */}
      <BarraMarcas dark={dark} marcas={marcas} />

      {facturas.length === 0 ? (
        <VacioDiscreto dark={dark} texto={t('salesOrders.detalle.sinFacturas')} />
      ) : visibles === 0 ? (
        <VacioDiscreto
          dark={dark}
          texto={t('salesOrders.detalle.sinFacturasFiltro')}
          accion={{ texto: t('common.clearFilters'), onClick: () => setBusqueda('') }}
        />
      ) : (
        <div className={`rounded-lg border overflow-hidden ${bordeCls(dark)}`}>
          <FacturasPedido
            sinTarjeta
            sinDetalle
            dark={dark}
            marcas={marcas}
            clavePedido={clavePedido}
            lineas={lineasVisibles}
            onVerAdjuntosFactura={onVerAdjuntosFactura}
            accionFactura={(factura) => (
              <button
                type="button"
                onClick={() => onVerLineas(factura)}
                className={btnNeutroCls(dark)}
              >
                {t('salesOrders.detalle.verSusLineas')}
              </button>
            )}
          />
          <PieTotales dark={dark} datos={totales} />
        </div>
      )}
    </div>
  )
}
